import React, { useEffect, useState } from "react";
import MainModal from "components/Modal/MainModal";
import { toast } from "react-toastify";
import { MdEdit, MdAdd, MdDelete } from "react-icons/md";
import { Editor } from "@tinymce/tinymce-react";
import { adminApiService } from "../../../services/api";

const FAQ = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFaqVisible, setIsFaqVisible] = useState(false);
  const [showFaqs, setShowFaqs] = useState(true);
  const [faqData, setFaqData] = useState({
    question: "",
    answer: "",
  });
  const [editingFaq, setEditingFaq] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [faqs, setFaqs] = useState([]);

  const fetchFaqs = async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getFaqs();
      if (!res.ok) throw new Error("Failed to fetch FAQs");
      const { data } = await res.json();
      setFaqs(data);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Error fetching FAQs.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    faqs.forEach((faq) => {
      setIsFaqVisible(faq.isvisible);
    });
  }, [faqs]);

  const handleFaqVisibilityToggle = async () => {
    try {
      const res = await adminApiService.toggleFaqVisibility({
        isVisible: !isFaqVisible,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to toggle FAQ visibility");
      }

      toast.success(`FAQs ${!isFaqVisible ? "shown" : "hidden"} successfully!`);
      await fetchFaqs();
    } catch (error) {
      toast.error(`Error toggling FAQ visibility: ${error.message}`);
      console.error("Toggle visibility error:", error);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFaqData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (content) => {
    setFaqData((prev) => ({ ...prev, answer: content }));
  };

  const openAddModal = () => {
    setIsAddMode(true);
    setEditingFaq(null);
    setFaqData({
      question: "",
      answer: "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (faq) => {
    setIsAddMode(false);
    setEditingFaq(faq);
    setFaqData({
      question: faq.question,
      answer: faq.answer,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!faqData.question.trim() || !faqData.answer.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      const res = isAddMode 
        ? await adminApiService.addFaq(faqData)
        : await adminApiService.updateFaq({
            ...faqData,
            id: editingFaq?.id,
          });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.message || `Failed to ${isAddMode ? "add" : "update"} FAQ`
        );
      }

      toast.success(`FAQ ${isAddMode ? "added" : "updated"} successfully!`);
      setIsModalOpen(false);
      await fetchFaqs();
      setFaqData({
        question: "",
        answer: "",
      });
      setEditingFaq(null);
      setIsAddMode(false);
    } catch (error) {
      toast.error(
        `Error ${isAddMode ? "adding" : "updating"} FAQ: ${error.message}`
      );
      console.error("Submit error:", error);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) {
      return;
    }

    try {
      const res = await adminApiService.deleteFaq(faq.id);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete FAQ");
      }

      toast.success("FAQ deleted successfully!");
      await fetchFaqs();
    } catch (error) {
      toast.error(`Error deleting FAQ: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                FAQ Management
              </h1>
              <p className="mt-3 text-base text-gray-700">
                Manage frequently asked questions and their answers
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={!isFaqVisible}
                    onChange={handleFaqVisibilityToggle}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {isFaqVisible ? "Hide FAQs" : "Show FAQs"}
                  </span>
                </label>
              </div>
              <button
                onClick={openAddModal}
                className="inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <MdAdd className="mr-2" size={24} />
                Add New FAQ
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-primary"></div>
            </div>
          ) : !showFaqs ? (
            <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900">
                FAQs are currently hidden
              </h3>
              <p className="mt-3 text-base text-gray-700">
                Toggle the switch above to show FAQs
              </p>
            </div>
          ) : faqs.length === 0 ? (
            <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
              <div className="rounded-full bg-primary/10 p-6">
                <MdAdd className="h-16 w-16 text-primary" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No FAQs Available
              </h3>
              <p className="mt-3 text-base text-gray-700">
                Get started by adding your first FAQ.
              </p>
              <button
                onClick={openAddModal}
                className="mt-8 inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <MdAdd className="mr-2" size={24} />
                Add New FAQ
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="rounded-2xl bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {faq.question}
                      </h2>
                      <div
                        className="prose prose-gray mt-4 max-w-none text-base text-gray-700"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                    <div className="ml-6 flex space-x-3">
                      <button
                        onClick={() => openEditModal(faq)}
                        className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                        aria-label="Edit FAQ"
                      >
                        <MdEdit size={24} />
                      </button>
                      <button
                        onClick={() => handleDelete(faq)}
                        className="rounded-xl p-2.5 text-red-600 transition-colors hover:bg-red-50"
                        aria-label="Delete FAQ"
                      >
                        <MdDelete size={24} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <MainModal
        isOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
        title={`${isAddMode ? "Add" : "Edit"} FAQ`}
        btnTitle={isAddMode ? "Add" : "Update"}
        actionbtn={handleSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="question"
              className="mb-3 block text-base font-medium text-gray-900"
            >
              Question *
            </label>
            <input
              type="text"
              id="question"
              name="question"
              value={faqData.question}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter your question..."
            />
          </div>
          <div>
            <label
              htmlFor="answer"
              className="mb-3 block text-base font-medium text-gray-900"
            >
              Answer *
            </label>
            <Editor
              apiKey={process.env.REACT_APP_TINY_MCE_EDITOR_API_KEY}
              value={faqData.answer}
              onEditorChange={handleEditorChange}
              init={{
                height: 300,
                menubar: false,
                plugins: [
                  "advlist",
                  "autolink",
                  "lists",
                  "link",
                  "image",
                  "charmap",
                  "preview",
                  "anchor",
                  "searchreplace",
                  "visualblocks",
                  "code",
                  "fullscreen",
                  "insertdatetime",
                  "media",
                  "table",
                  "code",
                  "help",
                  "wordcount",
                ],
                toolbar:
                  "undo redo | blocks | " +
                  "bold italic forecolor | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | " +
                  "removeformat | help",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; color: #374151; }",
                skin: "oxide",
                content_css: "default",
                branding: false,
                promotion: false,
                resize: false,
                statusbar: false,
                height: 400,
                width: "100%",
                toolbar_mode: "sliding",
                contextmenu: false,
                quickbars_selection_toolbar:
                  "bold italic | quicklink h2 h3 blockquote",
                quickbars_insert_toolbar: "quickimage quicktable",
                quickbars_image_toolbar:
                  "alignleft aligncenter alignright | rotateleft rotateright | imageoptions",
                toolbar_sticky: true,
                toolbar_sticky_offset: 64,
                autosave_ask_before_unload: true,
                autosave_interval: "30s",
                autosave_prefix: "{path}{query}-{id}-",
                autosave_restore_when_empty: false,
                autosave_retention: "2m",
                image_advtab: true,
                importcss_append: true,
                file_picker_callback: function (callback, value, meta) {
                  // Add your file picker logic here
                },
                templates: [
                  {
                    title: "New Table",
                    description: "creates a new table",
                    content:
                      '<div class="mceTmpl"><table width="98%%"  border="0" cellspacing="0" cellpadding="0"><tr><th scope="col"> </th><th scope="col"> </th></tr><tr><td> </td><td> </td></tr></table></div>',
                  },
                ],
                template_cdate_format:
                  "[Date Created (CDATE): %m/%d/%Y : %H:%M:%S]",
                template_mdate_format:
                  "[Date Modified (MDATE): %m/%d/%Y : %H:%M:%S]",
                height: 400,
                image_caption: true,
                quickbars_selection_toolbar:
                  "bold italic | quicklink h2 h3 blockquote quickimage quicktable",
                noneditable_noneditable_class: "mceNonEditable",
                toolbar_mode: "sliding",
                contextmenu:
                  "link image imagetools table configurepermanentpen",
                skin: "oxide",
                content_css: "default",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; color: #374151; }",
              }}
            />
          </div>
        </div>
      </MainModal>
    </div>
  );
};

export default FAQ;
